/** localStorage key for consumer SPA root font scale (5 levels). */
export const WI_FONT_SCALE_KEY = "wi-font-scale";

/** Relative steps from normal (index 0): −2 … +2. */
export const FONT_SCALE_LEVELS = [-2, -1, 0, 1, 2] as const;

export type FontScaleLevel = (typeof FONT_SCALE_LEVELS)[number];

/** Multipliers applied as `html { font-size: N% }` so rem-based type scales with chrome. */
export const FONT_SCALE_PERCENT: Record<FontScaleLevel, number> = {
	[-2]: 87.5,
	[-1]: 93.75,
	[0]: 100,
	[1]: 106.25,
	[2]: 112.5,
};

export const FONT_SCALE_LABELS: Record<FontScaleLevel, string> = {
	[-2]: "Smallest",
	[-1]: "Smaller",
	[0]: "Default",
	[1]: "Larger",
	[2]: "Largest",
};

export function isFontScaleLevel(value: unknown): value is FontScaleLevel {
	return typeof value === "number" && (FONT_SCALE_LEVELS as readonly number[]).includes(value);
}

export function parseFontScaleStored(raw: string | null): FontScaleLevel {
	if (raw == null || raw === "") return 0;
	const n = Number.parseInt(raw, 10);
	return isFontScaleLevel(n) ? n : 0;
}

export function readStoredFontScale(): FontScaleLevel {
	try {
		return parseFontScaleStored(localStorage.getItem(WI_FONT_SCALE_KEY));
	} catch {
		return 0;
	}
}

/** Sets data-font-scale + root font-size so rem utilities and chrome scale together. */
export function applyFontScale(level: FontScaleLevel): void {
	const root = document.documentElement;
	root.setAttribute("data-font-scale", String(level));
	root.style.fontSize = `${FONT_SCALE_PERCENT[level]}%`;
}

export function persistFontScale(level: FontScaleLevel): void {
	try {
		localStorage.setItem(WI_FONT_SCALE_KEY, String(level));
	} catch {
		/* private mode / blocked storage */
	}
}

export function applyAndPersistFontScale(level: FontScaleLevel): void {
	applyFontScale(level);
	persistFontScale(level);
}
